import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getStatus, patchStatus } from "@/lib/api";
import { LifegridMark, LifegridWordmark } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowLeft, Copy, PhoneCall, ShieldCheck, Share2, AlertTriangle } from "lucide-react";

const STATE_STYLE = {
  assistance: { color: "var(--lg-amber)", label: "ASSISTANCE REQUESTED" },
  safe: { color: "var(--lg-emerald)", label: "SAFE" },
  resolved: { color: "var(--lg-cyan)", label: "RESOLVED" },
};

export function PublicStatus() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getStatus(slug)
      .then(setS)
      .catch(() => setError("Status not found"));
  }, [slug]);

  const markSafe = async () => {
    setBusy(true);
    try {
      const next = await patchStatus(slug, "safe");
      setS(next);
      toast.success("Marked safe");
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "LIFEGRID Status", text: `Live status for ${s.name}`, url });
        return;
      } catch (e) {
        /* cancelled */
      }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen bg-[color:var(--lg-void)] text-[color:var(--lg-text)] flex flex-col">
      <header className="border-b border-white/10 backdrop-blur-2xl bg-[color:var(--lg-void)]/85 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[color:var(--lg-text-2)]" data-testid="status-back">
            <ArrowLeft size={16} /> <LifegridWordmark />
          </Link>
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">PUBLIC STATUS</span>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10 md:py-16">
        {error && (
          <div className="glass rounded-md p-8 text-center">
            <AlertTriangle className="mx-auto text-[color:var(--lg-amber)]" />
            <div className="mt-3 font-display text-2xl font-extrabold uppercase">Status not found</div>
            <p className="text-[color:var(--lg-text-2)] mt-2">This LIFEGRID status link may have expired or is invalid.</p>
            <Link to="/" className="btn-tactile primary mt-6 inline-flex" data-testid="status-home">
              Back to LIFEGRID
            </Link>
          </div>
        )}
        {!s && !error && (
          <div className="glass rounded-md p-8 text-center">
            <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
              <span className="live-dot inline-block align-middle mr-2" /> LOADING
            </div>
          </div>
        )}
        {s && (
          <div className="glass rounded-md p-6 md:p-8" data-testid="public-status-card">
            <div className="flex items-center justify-between">
              <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
                LIFEGRID STATUS
              </div>
              <div
                className="font-mono-tac text-[10px] tracking-[0.28em] px-2 py-1 border rounded-sm"
                style={{ color: STATE_STYLE[s.state]?.color, borderColor: STATE_STYLE[s.state]?.color }}
                data-testid="status-state"
              >
                {STATE_STYLE[s.state]?.label || s.state.toUpperCase()}
              </div>
            </div>
            <div className="mt-6">
              <div className="text-xs font-mono-tac tracking-[0.28em] text-[color:var(--lg-text-3)]">PERSON</div>
              <div className="mt-1 font-display text-3xl md:text-4xl font-extrabold uppercase">{s.name}</div>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Cell label="SITUATION" value={s.situation} />
              <Cell label="LOCATION" value={s.location || "Not shared"} />
              <Cell label="UPDATED" value={new Date(s.updated_at).toLocaleString()} />
              <Cell label="CREATED" value={new Date(s.created_at).toLocaleString()} />
            </div>
            <div className="mt-5">
              <div className="text-xs font-mono-tac tracking-[0.28em] text-[color:var(--lg-text-3)]">MESSAGE</div>
              <p className="mt-1 text-lg">{s.message}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              <a href="tel:112" className="btn-tactile danger" data-testid="status-call">
                <PhoneCall size={14} /> Contact
              </a>
              <button className="btn-tactile" onClick={share} data-testid="status-share">
                <Share2 size={14} /> Share
              </button>
              {s.state !== "safe" && (
                <button className="btn-tactile primary" onClick={markSafe} disabled={busy} data-testid="status-safe">
                  <ShieldCheck size={14} /> I&apos;m Safe Now
                </button>
              )}
              <button
                className="btn-tactile ghost"
                onClick={async () => {
                  await navigator.clipboard.writeText(window.location.href);
                  toast.success("Link copied");
                }}
                data-testid="status-copy"
              >
                <Copy size={14} /> Copy Link
              </button>
            </div>
            <p className="mt-8 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
              DEMO — LIFEGRID DOES NOT DISPATCH EMERGENCY SERVICES. THIS PAGE IS A SHAREABLE STATUS SIMULATION.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="border border-white/10 rounded-sm p-3">
      <div className="font-mono-tac text-[9px] tracking-[0.28em] text-[color:var(--lg-text-3)]">{label}</div>
      <div className="mt-1 truncate">{value}</div>
    </div>
  );
}
